<template>
    <transition name="slide-fade">
        <div
            v-if="systemAlerts.length > 0"
            class="system-alerts-container"
            :style="{ height: `${(showingAll ? systemAlerts.length : 1) * $systemAlertHeight}px` }"
        >
            <transition-group name="slide-fade">
                <system-alert
                    v-for="(alert, index) in systemAlerts"
                    v-show="showingAll || index === 0"
                    :key="alert.id"
                    :alert="alert"
                    :primary="index === 0"
                    :total-count="systemAlerts.length"
                    :showing-all="showingAll"
                    @dismissed="dismiss"
                    @toggle="toggle"
                />
            </transition-group>
        </div>
    </transition>
</template>

<script>
import { mapState } from 'vuex';

import SystemAlert from '@/components/SystemAlert.vue';
import { handleResponseError, initialState } from '@/Helpers.js';
import SystemAlertModel from '@/models/SystemAlert.js';
import axios from '@/plugins/axios.js';

export default {
    name: 'SystemAlerts',

    components: { SystemAlert },

    emits: ['toggled', 'alerts-changed'],

    data: () => ({
        dismissedAlerts: [],
        showingAll: false,
        listening: [],
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        echoReady() {
            return !!this.$store.state.Online.$echo;
        },

        systemAlerts() {
            return SystemAlertModel.query().withAll().get();
        },
    },

    watch: {
        echoReady(ready) {
            if (ready) {
                this.$echo.private(`App.Models.User.${this.user.id}`).notification(notification => {
                    if (notification.type === 'App\\Notifications\\SystemAlertNotification') {
                        SystemAlertModel.insert({ data: notification.systemAlert });
                    }
                });

                this.listenForSystemAlerts();
            }
        },

        systemAlerts: {
            handler(alerts) {
                // Pass the count so parents can clear layout offset immediately when
                // the last alert is dismissed (leave transitions can leave a ghost DOM node).
                this.$nextTick(() => {
                    this.$emit('alerts-changed', alerts.length);
                });
            },
            deep: true,
        },
    },

    async created() {
        const systemAlerts = initialState('SystemAlerts', null);

        if (systemAlerts.length > 0) {
            await SystemAlertModel.insert({
                data: systemAlerts,
            });
        }

        this.dismissedAlerts = JSON.parse(localStorage.getItem('dismissed-system-alerts')) || [];
    },

    beforeUnmount() {
        if (!this.$echo) {
            return;
        }

        if (this.user?.id) {
            this.$echo.leave(`App.Models.User.${this.user.id}`);
        }

        this.listening.forEach(channel => this.$echo.leave(channel));
    },

    methods: {
        dismiss(alert) {
            try {
                axios.delete(`/api/system-alerts/${alert.uuid}/dismiss`, { handleError: false });

                alert.$delete();

                this.dismissedAlerts.push(alert.id);
            } catch (error) {
                handleResponseError(error);
            }
        },

        toggle() {
            this.showingAll = !this.showingAll;

            this.$emit('toggled', this.showingAll);
            this.$nextTick(() => {
                this.$emit('alerts-changed', this.systemAlerts.length);
            });
        },

        listenForSystemAlerts() {
            const channels = [
                { name: 'public', validate: () => true },
                { name: 'students', validate: () => this.user.isStudentOfActiveCourse() },
                { name: 'instructors', validate: () => this.user.isInstructorOfActiveCourse() },
                { name: 'group-instructors', validate: () => this.user.isGroupInstructor() },
                { name: 'student-support', validate: () => this.user.isStudentSupport() },
                { name: 'admins', validate: () => this.user.is_admin },
            ];

            channels
                .filter(channel => channel.validate())
                .forEach(channel => {
                    this.$echo.channel(`system-alerts-${channel.name}`).listen('SystemAlertEvent', event => {
                        SystemAlertModel.insert({ data: event.systemAlert });
                    });

                    this.listening.push(`system-alerts-${channel.name}`);
                });
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.system-alerts-container {
    width: 100%;
    overflow: hidden;
    display: block;
    visibility: visible;

    // Alert is in normal flow within fixed header
    // Need higher z-index than navigation (z-index: 12) to receive clicks
    // Navigation is positioned below alert via top: calc(var(--app-header-alert-height) + 1px)
    // Apply z-index fix for both mobile and desktop to ensure clicks work
    position: relative;
    z-index: 20; // Higher than navigation's z-index: 12 to ensure clicks work
}

//.slide-fade-enter-active {
//    transition: all 0.3s ease-out;
//}
//
//.slide-fade-leave-active {
//    transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
//}
//
//.slide-fade-enter-from,
//.slide-fade-leave-to {
//    transform: translateY(-35px);
//    opacity: 0;
//}
</style>
